import { useState, useEffect } from "react";
import { Autocomplete, TextField, CircularProgress } from "@mui/material";
import useApi from "../../hooks/useApi";
import { getId, idEquals } from "../../utils/idHelpers";

const AutoSearch = ({
  value,
  onChange,
  label = "Search",
  entityName,
  action,
  disabled = false,
  size = "small",
  minChars = 2,
}) => {
  const { postData } = useApi();
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [inputValue, setInputValue] = useState("");
  const [selectedValue, setSelectedValue] = useState(null);

  // Load the selected value when value prop changes
  useEffect(() => {
    if (value && !selectedValue) {
      // Fetch the full object for the selected ID
      const loadSelected = async () => {
        try {
          const response = await postData({
            entityName: entityName,
            action: action,
            data: { searchString: "" },
          });

          if (response?.isSuccessful && response.data) {
            const selected = response.data.find((opt) => opt._id === value);
            if (selected) {
              setSelectedValue(selected);
            }
          }
        } catch (error) {
          console.error(`Error loading selected ${entityName}:`, error);
        }
      };
      loadSelected();
    } else if (!value) {
      setSelectedValue(null);
    }
  }, [value]);

  // Debounce search to avoid excessive API calls
  useEffect(() => {
    if (!inputValue || inputValue.length < minChars) {
      setOptions([]);
      return;
    }

    const timeoutId = setTimeout(async () => {
      setLoading(true);
      try {
        const response = await postData({
          entityName: entityName,
          action: action,
          data: { searchString: inputValue },
        });

        if (response?.isSuccessful && response.data) {
          setOptions(response.data);
        } else {
          setOptions([]);
        }
      } catch (error) {
        console.error(`Error searching ${entityName}:`, error);
        setOptions([]);
      } finally {
        setLoading(false);
      }
    }, 300); // 300ms debounce delay

    return () => clearTimeout(timeoutId);
  }, [inputValue, entityName, action, minChars]);

  const handleChange = (event, newValue) => {
    setSelectedValue(newValue);
    onChange(getId(newValue));
  };

  const handleInputChange = (event, newInputValue) => {
    setInputValue(newInputValue);
  };

  return (
    <Autocomplete
      value={selectedValue}
      onChange={handleChange}
      onInputChange={handleInputChange}
      options={Array.isArray(options) ? options : []}
      getOptionLabel={(option) => option.name || ""}
      loading={loading}
      disabled={disabled}
      size={size}
      isOptionEqualToValue={(option, val) => idEquals(option, getId(val))}
      noOptionsText={
        inputValue.length < minChars
          ? `Type at least ${minChars} characters to search`
          : `No ${label.toLowerCase()} found`
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          slotProps={{
            input: {
              ...params.InputProps,
              endAdornment: (
                <>
                  {loading ? (
                    <CircularProgress
                      color="inherit"
                      size={20}
                    />
                  ) : null}
                  {params.InputProps.endAdornment}
                </>
              ),
            },
          }}
        />
      )}
    />
  );
};

export default AutoSearch;
